import { useEffect, useState } from "react";
import { X, Chrome, MousePointerClick } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

const EXTENSION_ID = "enjgpnmebahcbmkgnndjbmgceocelgpa";
const EXTENSION_URL = "https://chromewebstore.google.com/detail/tavlo-save-to-your-feed/enjgpnmebahcbmkgnndjbmgceocelgpa";

export function ExtensionPromoBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check if dismissed
    const isDismissed = localStorage.getItem("tavlo_extension_banner_dismissed_v2");
    if (isDismissed) {
      setIsChecking(false);
      return;
    }

    // Check if browser is Chrome (excluding Edge, Opera etc if possible, but basic check is fine)
    const isChrome = 
      typeof window !== "undefined" && 
      /Chrome/.test(navigator.userAgent) && 
      /Google Inc/.test(navigator.vendor);

    if (!isChrome) {
      setIsChecking(false);
      return;
    }

    // Chrome is detected, let's ping the extension
    const checkExtensionInstalled = async () => {
      try {
        // @ts-expect-error - chrome runtime might be missing
        if (!window.chrome || !window.chrome.runtime) {
          setIsVisible(true);
          setIsChecking(false);
          return;
        }

        const pingExtension = (attempt = 1, maxAttempts = 3) => {
          // @ts-expect-error - external message to extension
          window.chrome.runtime.sendMessage(
            EXTENSION_ID,
            { type: "TAVLO_EXTENSION_PING" },
            (response: any) => {
              // @ts-expect-error - chrome runtime error
              const lastError = window.chrome.runtime.lastError;
              
              if (lastError) {
                const errorMessage = lastError.message || "";
                
                // If service worker is sleeping, wait and retry
                if (errorMessage.includes("Receiving end does not exist") && attempt < maxAttempts) {
                  setTimeout(() => pingExtension(attempt + 1, maxAttempts), attempt * 400);
                  return;
                }
                
                // If the extension is definitely not installed or didn't wake up
                if (
                  errorMessage.includes("Invalid extension id") || 
                  errorMessage.includes("does not specify this page") ||
                  errorMessage.includes("Receiving end does not exist")
                ) {
                  setIsVisible(true);
                } else {
                  // Other errors (like "message port closed before a response") mean it IS installed
                  setIsVisible(false);
                }
              } else {
                // We got a response, it's installed
                setIsVisible(false);
              }
              
              setIsChecking(false);
            }
          );
        };

        pingExtension();
      } catch (error) {
        setIsVisible(true);
        setIsChecking(false);
      }
    };

    // Small delay to ensure extension has time to initialize
    setTimeout(checkExtensionInstalled, 500);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("tavlo_extension_banner_dismissed_v2", "true");
    setIsVisible(false);
  };

  if (isChecking) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <div className="bg-zinc-950 border-b border-white/[0.08] relative">
            <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 via-fuchsia-500/5 to-transparent pointer-events-none" />
            <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 relative z-10">
              
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(139,92,246,0.2)]">
                  <Chrome className="w-5 h-5 text-violet-400" />
                </div>
                <div className="flex flex-col">
                  <p className="text-sm font-bold text-zinc-100 flex items-center gap-2">
                    Tavlo Chrome Extension
                    <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full bg-fuchsia-500/20 text-fuchsia-400 text-[10px] uppercase tracking-wider font-extrabold border border-fuchsia-500/30">
                      Recommended
                    </span>
                  </p>
                  <p className="text-xs text-zinc-400 max-w-lg mt-0.5">
                    Save articles, tweets, and inspiration with one click directly from your browser.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
                <Button
                  onClick={() => window.open(EXTENSION_URL, "_blank")}
                  className="flex-1 sm:flex-none bg-white hover:bg-zinc-200 text-zinc-950 rounded-xl text-xs font-bold px-4 py-2 h-9 transition-colors shadow-lg shadow-white/5"
                >
                  <MousePointerClick className="w-3.5 h-3.5 mr-2" />
                  Install Now
                </Button>
                <button
                  onClick={handleDismiss}
                  className="p-2 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-white/5 transition-colors"
                  aria-label="Dismiss banner"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
